import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DisplayService } from '../../../../api/gen/api/display.service';
import { DisplayTemplateService } from '../../../../api/gen/api/displayTemplate.service';
import { ParkingSpaceService } from '../../../../api/gen/api/parkingSpace.service';
import { OperatorActionsService } from '../../../../api/gen/api/operatorActions.service';
import { DisplayWithHealthDto, ParkingSpaceDto } from '../../../../api/gen';

@Component({
    selector: 'app-clone-template-modal',
    standalone: true,
    imports: [
        FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule,
        MatInputModule, MatSelectModule, MatTabsModule, MatProgressSpinnerModule
    ],
    templateUrl: './clone-template-modal.component.html',
    styleUrl: './clone-template-modal.component.scss'
})
export class CloneTemplateModalComponent implements OnChanges {
    private readonly displayService = inject(DisplayService);
    private readonly templateService = inject(DisplayTemplateService);
    private readonly parkingSpaceService = inject(ParkingSpaceService);
    private readonly operatorActionsService = inject(OperatorActionsService);
    private readonly snackBar = inject(MatSnackBar);

    @Input() templateId!: string;
    @Input() sourceDisplayId!: string;
    @Input() siteId: string | null = null;
    @Output() closed = new EventEmitter<void>();
    @Output() cloned = new EventEmitter<void>();

    // Clone to display
    displays = signal<DisplayWithHealthDto[]>([]);
    loadingDisplays = signal(false);
    selectedDisplayId = '';
    /** Site whose displays are listed as clone targets. Defaults to the source site but can be any site. */
    targetSiteId = '';
    newName = '';
    cloning = signal(false);

    // Apply to site
    sites = signal<ParkingSpaceDto[]>([]);
    loadingSites = signal(false);
    selectedSiteId = '';
    applying = signal(false);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['siteId'] || changes['sourceDisplayId']) {
            this.targetSiteId = this.siteId ?? '';
            this.loadDisplays();
            this.loadSites();
        }
    }

    /** Reload the target-display list when the user picks a different site to clone into. */
    onTargetSiteChange(): void {
        this.selectedDisplayId = '';
        this.loadDisplays();
    }

    private loadDisplays(): void {
        if (!this.targetSiteId) {
            this.displays.set([]);
            return;
        }
        this.loadingDisplays.set(true);
        this.displayService.displayGet(this.targetSiteId, undefined, undefined, undefined, undefined, 200)
            .subscribe({
                next: result => {
                    this.displays.set((result?.items ?? []).filter(d => d.displayId !== this.sourceDisplayId));
                    this.loadingDisplays.set(false);
                },
                error: () => {
                    this.snackBar.open('Failed to load displays', 'Close', { duration: 3000 });
                    this.loadingDisplays.set(false);
                }
            });
    }

    private loadSites(): void {
        this.loadingSites.set(true);
        // Scope the selectable sites to the parking spaces this operator is authorized for.
        // Admins have no operator record (or no assigned spaces), so they fall back to the full list.
        this.operatorActionsService.operatorMyAccountGet().subscribe({
            next: account => {
                const mySpaces = account?.parkingSpaces ?? [];
                if (mySpaces.length > 0) {
                    this.sites.set(mySpaces);
                    this.loadingSites.set(false);
                } else {
                    this.loadAllSites();
                }
            },
            error: () => this.loadAllSites()
        });
    }

    private loadAllSites(): void {
        this.parkingSpaceService.parkingSpaceFilterPost(
            undefined, undefined, undefined, undefined, undefined, undefined, 0, 100
        ).subscribe({
            next: result => {
                this.sites.set(result?.items ?? []);
                this.loadingSites.set(false);
            },
            error: () => {
                this.snackBar.open('Failed to load sites', 'Close', { duration: 3000 });
                this.loadingSites.set(false);
            }
        });
    }

    async cloneToDisplay(): Promise<void> {
        if (!this.selectedDisplayId) return;
        this.cloning.set(true);
        try {
            await firstValueFrom(
                this.templateService.displayTemplateIdClonePost(this.templateId, {
                    targetDisplayId: this.selectedDisplayId,
                    newName: this.newName.trim() || undefined
                })
            );
            this.snackBar.open('Template cloned successfully', 'Close', { duration: 2000 });
            this.cloned.emit();
        } catch {
            this.snackBar.open('Failed to clone template', 'Close', { duration: 3000 });
        } finally {
            this.cloning.set(false);
        }
    }

    async applyToSite(): Promise<void> {
        if (!this.selectedSiteId) return;
        this.applying.set(true);
        try {
            await firstValueFrom(
                this.templateService.displayTemplateIdApplyToSitePost(this.templateId, {
                    parkingSpaceId: this.selectedSiteId
                })
            );
            this.snackBar.open('Template applied to site', 'Close', { duration: 2000 });
            this.cloned.emit();
        } catch {
            this.snackBar.open('Failed to apply template to site', 'Close', { duration: 3000 });
        } finally {
            this.applying.set(false);
        }
    }

    getSiteName(siteId: string): string {
        return this.sites().find(s => s.id === siteId)?.name ?? siteId;
    }
}
